/**
 * Thresholds for the Prisma query instrumentation extension.
 *
 * All are overridable by env var so they can be tuned in production without a
 * code deploy, and are read once when the extension is created (i.e. once per
 * Prisma client construction) to keep the per-query path free of env lookups.
 * Every one is genuinely optional: absent is the normal case, and each falls
 * back to the default below.
 */
export const ROW_COUNT_THRESHOLD_ENV_VAR = "PRISMA_SLOW_QUERY_ROW_THRESHOLD";
export const DURATION_MS_THRESHOLD_ENV_VAR = "PRISMA_SLOW_QUERY_MS_THRESHOLD";

export const DEFAULT_ROW_COUNT_THRESHOLD = 5000;
export const DEFAULT_DURATION_MS_THRESHOLD = 2000;

/**
 * Kill switch for the expensive half of the instrument. Set to `"true"` to
 * keep the existing row-count/duration logging but skip the result-graph walk
 * entirely, without a deploy. Anything else (including absent) leaves the walk
 * on.
 */
export const HYDRATION_WALK_DISABLED_ENV_VAR = "PRISMA_HYDRATION_WALK_DISABLED";

/**
 * How many hydrated nodes a query must produce before it is worth a log line
 * on its own — i.e. when neither existing threshold fired.
 *
 * The two OOMs this was built for ramped ~2.9 GB while the largest query
 * logged 17,336 top-level rows, so the interesting queries are well under the
 * row threshold and well over this one.
 */
export const HYDRATED_NODE_THRESHOLD_ENV_VAR = "PRISMA_HYDRATED_NODE_THRESHOLD";
export const DEFAULT_HYDRATED_NODE_THRESHOLD = 50_000;

/**
 * The cheap secondary trigger for the relation-aware path.
 *
 * A query below this and below both logging thresholds is left completely
 * alone: no walk, no argument inspection, no `memoryUsage()` call, no
 * allocation. It is deliberately far below the row threshold, because the
 * whole point is to catch the query that returns few rows and hydrates a
 * fortune.
 *
 * **Row count only — deliberately not duration.** Row count is a property of
 * the query; duration is a property of the pod's health. During an OOM ramp,
 * GC pressure stalls the event loop badly enough that bounded queries
 * (`take: 10`) have been observed taking 4.5-7s in production. A duration
 * trigger would therefore fire on nearly every query at precisely the moment
 * the pod has no CPU to spare, making the instrument most expensive when the
 * process is closest to death. Slow queries still get the tier-1 log line;
 * they just do not get walked.
 */
export const RELATION_WALK_ROW_TRIGGER_ENV_VAR =
  "PRISMA_RELATION_WALK_ROW_TRIGGER";
export const DEFAULT_RELATION_WALK_ROW_TRIGGER = 100;

/**
 * Ceiling on how many result-graph walks may run per second, per Prisma
 * client.
 *
 * The walk is synchronous, so this bounds the instrument's worst-case
 * blocking CPU to a constant that does not scale with load or with how close
 * the pod is to dying. Skipped walks are counted and reported on the next log
 * line (`hydrationWalksSkipped`), so rate limiting shows up in the data
 * rather than hiding in it.
 */
export const WALKS_PER_SECOND_ENV_VAR = "PRISMA_HYDRATION_WALKS_PER_SECOND";
export const DEFAULT_WALKS_PER_SECOND = 2;

/** The dd-trace tag that holds a span's resource name (e.g. the route). */
export const DD_RESOURCE_NAME_TAG = "resource.name";

export const SLOW_QUERY_LOG_MESSAGE = "Prisma query exceeded threshold";
